// Magic Forms tools: list, read, create and edit forms and their fields
import { tool } from "ai"
import { z } from "zod"
import { supabase } from "@/lib/agents/db"
import { validateFormula, FORMULA_FUNCTIONS } from "@/lib/agents/formlogic"
import { buildFormSystemPrompt } from "./prompt"

const listItemSchema = z.object({
  label: z.string(),
  value: z.string(),
})

const subFieldSchema = z.object({
  name: z.string().max(64).regex(/^[a-z0-9_]+$/, "name must be lowercase letters, digits and underscores only"),
  title: z.string(),
  type: z.enum(["string", "number", "boolean", "date", "list", "multiselect"]),
  required: z.boolean().nullable(),
  list: z.array(listItemSchema).nullable().describe("Options for list / multiselect sub-fields"),
})

const fieldSchema = z.object({
  name: z
    .string()
    .max(64)
    .regex(/^[a-z0-9_]+$/, "name must be lowercase letters, digits and underscores only")
    .describe("Machine key for the field (a-z, 0-9, _). Becomes a dashboard variable."),
  title: z.string().describe("Human-readable label shown in the form UI"),
  type: z
    .enum(["string", "number", "boolean", "date", "list", "multiselect", "group"])
    .describe('"date" = calendar date (stored as ISO "YYYY-MM-DD"); "list" = single-select; "multiselect" = choose several; "group" = a repeating object (list of entries, each with subfields)'),
  required: z.boolean().nullable().describe("Whether the field must be filled"),
  list: z
    .array(listItemSchema)
    .nullable()
    .describe("Options for list / multiselect fields (label + value pairs)"),
  subfields: z
    .array(subFieldSchema)
    .nullable()
    .describe('For type "group": the fields that make up each repeating entry, e.g. a "dependents" group with subfields name, age, relationship.'),
  extraction: z
    .object({
      enabled: z.boolean().describe("Whether the agent may auto-fill this field from a conversation"),
      method: z.enum(["ai", "keyword"]).nullable().describe('"ai" (LLM + instruction) or "keyword" (term matching). Defaults to ai.'),
      instruction: z.string().describe("AI method: how the agent should fill this field. Give a short sentence even for keyword fields."),
      minConfidence: z.number().min(0).max(1).nullable().describe("AI method: discard extractions below this confidence (0-1)"),
      lockAfterHuman: z.boolean().nullable().describe("Once a human edits the field, never let the agent overwrite it"),
      keywords: z.array(z.string()).nullable().describe("Keyword method: terms to look for in the conversation"),
      keywordMatch: z.enum(["any", "all", "phrase"]).nullable().describe("Keyword method: match any/all keywords, or phrase substring"),
      keywordValue: z.string().nullable().describe("Keyword method: the value to store when a keyword matches (e.g. \"high\"). Omit to store true as a flag."),
    })
    .nullable()
    .describe("Magic Form config — omit for fields the agent should NOT auto-fill"),
  visibleWhen: z
    .array(
      z.object({
        field: z.string().describe("name of another field on this form"),
        operator: z.enum(["equals", "notEquals", "contains", "notContains", "gt", "lt", "gte", "lte", "isEmpty", "isNotEmpty"])
          .describe("gt=greater than, lt=less than, gte=greater-or-equal, lte=less-or-equal"),
        value: z.union([z.string(), z.number(), z.boolean()]).nullable(),
      })
    )
    .nullable()
    .describe("Conditional visibility: show/require this field only when ALL these conditions on other fields hold"),
  calculated: z
    .object({
      formula: z
        .string()
        .describe(
          'Expression in the form DSL (NOT JavaScript). Reference fields as {field_name}. ' +
            'Supports + - * / , parentheses, string concat, comparisons (== != > < >= <=), ' +
            'boolean && ||, and a ternary cond ? a : b. To aggregate a repeating group use ' +
            'sum/avg/count/min/max(group_name, subfield_name), e.g. sum(income_sources, income_amount). ' +
            'count(group_name) counts entries. NO .map/.reduce/.filter/arrow functions. ' +
            'Examples: "{qty} * {price}", "{first_name} + \\" \\" + {last_name}", ' +
            '"sum(income) - sum(expenses, amount)", "{net} > 0 ? {net} : 0". ' +
            'May reference other calculated fields.',
        ),
    })
    .nullable()
    .describe("Calculated field: value is derived from other fields (read-only, not extracted)"),
})

export type FormFieldInput = z.infer<typeof fieldSchema>

export interface PendingFormAction {
  kind: "update_form" | "add_field" | "update_field" | "remove_field" | "delete_form"
  formTitle: string
  summary: string
  detail?: Record<string, any>
}

export interface FormToolCallbacks {
  teamId?: string
  onFormsChanged?: () => void
  onApprove?: (action: PendingFormAction) => Promise<boolean>
}

// Recursively drops nulls before saving. Schemas are .nullable() (strict mode needs every property), so unset fields arrive as null
function stripNulls<T>(value: T): T {
  if (Array.isArray(value)) return value.map(stripNulls) as unknown as T
  if (value && typeof value === "object") {
    const out: Record<string, any> = {}
    for (const [k, v] of Object.entries(value as Record<string, any>)) {
      if (v === null) continue
      out[k] = stripNulls(v)
    }
    return out as T
  }
  return value
}

function validateFields(fields: FormFieldInput[]): string | null {
  const names = new Set<string>()
  for (const f of fields) {
    if (names.has(f.name)) return `Duplicate field name "${f.name}" — names must be unique within a form.`
    names.add(f.name)
    if ((f.type === "list" || f.type === "multiselect") && (!f.list || f.list.length === 0)) {
      return `Field "${f.name}" is type ${f.type} but has no options — add at least one list item.`
    }
    if (f.type === "group") {
      if (!f.subfields || f.subfields.length === 0) {
        return `Group field "${f.name}" needs at least one sub-field.`
      }
      const subNames = new Set<string>()
      for (const sf of f.subfields) {
        if (subNames.has(sf.name)) return `Group "${f.name}" has duplicate sub-field name "${sf.name}".`
        subNames.add(sf.name)
        if ((sf.type === "list" || sf.type === "multiselect") && (!sf.list || sf.list.length === 0)) {
          return `Sub-field "${sf.name}" in group "${f.name}" is type ${sf.type} but has no options.`
        }
      }
      if (f.calculated?.formula) return `Group field "${f.name}" can't be calculated.`
    }
    if (f.extraction?.enabled && f.extraction.method !== "keyword" && !f.extraction.instruction?.trim()) {
      return `Field "${f.name}" uses AI extraction but has no instruction — the agent needs guidance to fill it.`
    }
    if (f.extraction?.enabled && f.extraction.method === "keyword" && !f.extraction.keywords?.length) {
      return `Field "${f.name}" uses keyword extraction but has no keywords.`
    }
    if (f.calculated?.formula && f.extraction?.enabled) {
      return `Field "${f.name}" is calculated, so it can't also be agent-filled — remove its extraction config.`
    }
    if (f.calculated?.formula) {
      const formulaErr = validateFormula(f.calculated.formula)
      if (formulaErr) return `Field "${f.name}" has an invalid formula: ${formulaErr}`
    }
  }
  return null
}

export function buildFormTools({ teamId, onFormsChanged, onApprove }: FormToolCallbacks) {
  const changed = () => onFormsChanged?.()
  const rejected = { status: "rejected" as const, message: "The user rejected this change. Ask what they'd like different." }
  const approve = async (action: PendingFormAction) => (onApprove ? onApprove(action) : true)

  // Serialize mutations: the SDK runs a step's tool calls in parallel, causing a read-modify-write race on the shared fields array.
  let mutationQueue: Promise<any> = Promise.resolve()
  const serialize = <T>(fn: () => Promise<T>): Promise<T> => {
    const run = mutationQueue.then(fn, fn)
    mutationQueue = run.then(() => undefined, () => undefined)
    return run
  }

  return {
    list_forms: tool({
      description: "List all forms for the current team with a short summary of each.",
      parameters: z.object({}),
      execute: async () => {
        let q = supabase.from("forms").select("id, title, fields, created_at")
        if (teamId) q = q.eq("team", teamId)
        const { data, error } = await q.order("created_at", { ascending: false })
        if (error) return { error: error.message }
        return (data || []).map((f) => ({
          id: f.id,
          title: f.title || "(untitled)",
          field_count: ((f.fields as unknown as any[]) || []).length,
          ai_fields: ((f.fields as unknown as FormFieldInput[]) || []).filter((x) => x.extraction?.enabled).length,
        }))
      },
    }),

    get_form: tool({
      description: "Get the full definition of one form, including every field and its extraction config.",
      parameters: z.object({
        form_id: z.string().nullable().describe("Exact form id (uuid)"),
        form_title: z.string().nullable().describe("Match by title (case-insensitive substring) if id is unknown"),
      }),
      execute: async ({ form_id, form_title }) => {
        const found = await findForm(teamId, form_id, form_title)
        if ("error" in found) return found
        const f = found.form
        return { id: f.id, title: f.title, fields: (f.fields as unknown as FormFieldInput[]) || [] }
      },
    }),

    create_form: tool({
      description:
        "Create a new form. Provide a title and the full list of fields. Enable `extraction` only on fields the agent should auto-fill from conversations.",
      parameters: z.object({
        title: z.string(),
        fields: z.array(fieldSchema).describe("The form's fields"),
      }),
      execute: ({ title, fields }) => serialize(async () => {
        const invalid = validateFields(fields)
        if (invalid) return { error: invalid }
        const { data, error } = await supabase
          .from("forms")
          .insert({ title, fields: stripNulls(fields) as any, team: teamId ?? null })
          .select("id, title")
          .single()
        if (error) return { error: error.message }
        changed()
        return { success: true, form_id: data.id, title: data.title }
      }),
    }),

    update_form: tool({
      description:
        "Replace a form's title and/or its entire fields array. To tweak a single field prefer add_field / update_field / remove_field.",
      parameters: z.object({
        form_id: z.string().nullable(),
        form_title: z.string().nullable(),
        title: z.string().nullable().describe("New title (omit to keep current)"),
        fields: z.array(fieldSchema).nullable().describe("Full replacement fields array (omit to keep current)"),
      }),
      execute: ({ form_id, form_title, title, fields }) => serialize(async () => {
        const found = await findForm(teamId, form_id, form_title)
        if ("error" in found) return found
        if (fields) {
          const invalid = validateFields(fields)
          if (invalid) return { error: invalid }
        }
        // null means "not supplied", not "clear it"
        const patch: Record<string, any> = {}
        if (title != null) patch.title = title
        if (fields != null) patch.fields = stripNulls(fields)
        if (Object.keys(patch).length === 0) return { error: "Nothing to update." }
        const summaryParts: string[] = []
        if (title != null) summaryParts.push(`rename to "${title}"`)
        if (fields != null) summaryParts.push(`replace all fields (${fields.length} field${fields.length !== 1 ? "s" : ""})`)
        if (!(await approve({ kind: "update_form", formTitle: found.form.title, summary: summaryParts.join("; "), detail: patch }))) return rejected
        const { error } = await supabase.from("forms").update(patch).eq("id", found.form.id)
        if (error) return { error: error.message }
        changed()
        return { success: true, form_id: found.form.id }
      }),
    }),

    add_field: tool({
      description: "Add a single field to an existing form.",
      parameters: z.object({
        form_id: z.string().nullable(),
        form_title: z.string().nullable(),
        field: fieldSchema,
      }),
      execute: ({ form_id, form_title, field }) => serialize(async () => {
        const found = await findForm(teamId, form_id, form_title)
        if ("error" in found) return found
        const current = (found.form.fields as unknown as FormFieldInput[]) || []
        const next = [...current, stripNulls(field)]
        const invalid = validateFields(next)
        if (invalid) return { error: invalid }
        if (!(await approve({ kind: "add_field", formTitle: found.form.title, summary: `add field "${field.name}" (${field.type})`, detail: { field } }))) return rejected
        const { error } = await supabase.from("forms").update({ fields: next as any }).eq("id", found.form.id)
        if (error) return { error: error.message }
        changed()
        return { success: true, added_field: field.name, form_id: found.form.id }
      }),
    }),

    update_field: tool({
      description: "Update one field on a form. Pass only the properties to change; the field is matched by name.",
      parameters: z.object({
        form_id: z.string().nullable(),
        form_title: z.string().nullable(),
        field_name: z.string().describe("The name of the field to update"),
        // Not .partial(): strict schemas reject non-required properties. Unchanged props arrive as null and are stripped
        changes: fieldSchema.describe("Properties to change on the field. Send null for anything you are not changing."),
      }),
      execute: ({ form_id, form_title, field_name, changes }) => serialize(async () => {
        const found = await findForm(teamId, form_id, form_title)
        if ("error" in found) return found
        const current = (found.form.fields as unknown as FormFieldInput[]) || []
        const idx = current.findIndex((f) => f.name === field_name)
        if (idx === -1) return { error: `Field "${field_name}" not found on form "${found.form.title}".` }
        const next = [...current]
        next[idx] = { ...next[idx], ...stripNulls(changes) } as FormFieldInput
        const invalid = validateFields(next)
        if (invalid) return { error: invalid }
        if (!(await approve({ kind: "update_field", formTitle: found.form.title, summary: `update field "${field_name}"`, detail: { field_name, changes } }))) return rejected
        const { error } = await supabase.from("forms").update({ fields: next as any }).eq("id", found.form.id)
        if (error) return { error: error.message }
        changed()
        return { success: true, updated_field: next[idx].name, form_id: found.form.id }
      }),
    }),

    remove_field: tool({
      description: "Remove a field from a form by name.",
      parameters: z.object({
        form_id: z.string().nullable(),
        form_title: z.string().nullable(),
        field_name: z.string(),
      }),
      execute: ({ form_id, form_title, field_name }) => serialize(async () => {
        const found = await findForm(teamId, form_id, form_title)
        if ("error" in found) return found
        const current = (found.form.fields as unknown as FormFieldInput[]) || []
        const next = current.filter((f) => f.name !== field_name)
        if (next.length === current.length) return { error: `Field "${field_name}" not found.` }
        if (!(await approve({ kind: "remove_field", formTitle: found.form.title, summary: `remove field "${field_name}"`, detail: { field_name } }))) return rejected
        const { error } = await supabase.from("forms").update({ fields: next as any }).eq("id", found.form.id)
        if (error) return { error: error.message }
        changed()
        return { success: true, removed_field: field_name, form_id: found.form.id }
      }),
    }),

    reorder_fields: tool({
      description:
        "Reorder the fields on a form. Provide the field names in the desired order (all existing top-level field names must be included).",
      parameters: z.object({
        form_id: z.string().nullable(),
        form_title: z.string().nullable(),
        order: z.array(z.string()).describe("Field names in the new order — must contain exactly the form's current field names"),
      }),
      execute: ({ form_id, form_title, order }) => serialize(async () => {
        const found = await findForm(teamId, form_id, form_title)
        if ("error" in found) return found
        const current = (found.form.fields as unknown as FormFieldInput[]) || []
        const names = current.map((f) => f.name)
        const missing = names.filter((n) => !order.includes(n))
        const unknown = order.filter((n) => !names.includes(n))
        if (missing.length || unknown.length) {
          return { error: `Order must list exactly the current fields. Missing: [${missing.join(", ")}]; unknown: [${unknown.join(", ")}]. Current order: [${names.join(", ")}]` }
        }
        const byName = new Map(current.map((f) => [f.name, f]))
        const next = order.map((n) => byName.get(n)!)
        if (!(await approve({ kind: "update_form", formTitle: found.form.title, summary: `reorder fields to: ${order.join(", ")}`, detail: { order } }))) return rejected
        const { error } = await supabase.from("forms").update({ fields: next as any }).eq("id", found.form.id)
        if (error) return { error: error.message }
        changed()
        return { success: true, form_id: found.form.id, order }
      }),
    }),

    list_agents: tool({
      description:
        "List the AI agents in this project. Use this to understand what the team's agents do so you can suggest relevant form fields.",
      parameters: z.object({
        include_archived: z.boolean().nullable().describe("Include archived agents (default false)"),
      }),
      execute: async ({ include_archived }) => {
        let q = supabase.from("agents").select("id, title, description, status")
        if (teamId) q = q.eq("team_id", teamId)
        const { data, error } = await q
        if (error) return { error: error.message }
        return (data || [])
          .filter((a) => include_archived || (a.status || "active") !== "archived")
          .map((a) => ({ id: a.id, title: a.title, description: a.description, status: a.status || "active" }))
      },
    }),

    get_agent_details: tool({
      description:
        "Inspect one agent's FULL configuration so you can understand everything it collects and design forms from it. Returns every worker/node with its type, all input/output handles (names, titles, prompts, enums), and its complete parameters — including onboarding questions (label, question text, options), escalation flags, list options, instructions/prompts, and any extraction fields. Use the `onboarding` breakout on onboard nodes to turn each question into a form field.",
      parameters: z.object({
        agent_id: z.number().nullable(),
        agent_title: z.string().nullable().describe("Match by title if id is unknown"),
      }),
      execute: async ({ agent_id, agent_title }) => {
        let q = supabase.from("agents").select("id, title, description, workers, config")
        if (teamId) q = q.eq("team_id", teamId)
        if (agent_id != null) q = q.eq("id", agent_id)
        const { data, error } = await q
        if (error) return { error: error.message }
        let agent = agent_id != null ? (data || [])[0] : undefined
        if (!agent && agent_title) {
          const needle = agent_title.toLowerCase()
          agent = (data || []).find((a) => (a.title || "").toLowerCase().includes(needle))
        }
        if (!agent) return { error: "Agent not found." }

        const workers = (agent.workers as unknown as any[]) || []
        const nodes = workers.map((w) => {
          const handlesRaw = w.handles ? (Object.values(w.handles) as any[]) : []
          const handles = handlesRaw
            .filter((h) => h && !h.system)
            .map((h) => ({
              name: h.name,
              title: h.title,
              type: h.type,
              direction: h.direction,
              prompt: typeof h.prompt === "string" ? h.prompt.slice(0, 1000) : undefined,
              enum: h.enum,
              condition: h.condition || undefined,
            }))

          const params = w.parameters || {}

          const onboarding =
            w.type === "onboard" && Array.isArray(params.items)
              ? params.items.map((it: any) => ({
                  name: it.name,
                  title: it.title,
                  question: it.question,
                  summarize: it.summarize,
                  options: Array.isArray(it.options)
                    ? it.options.map((o: any) => ({ label: o.label, answer: o.answer, action: o.action, goto: o.goto }))
                    : undefined,
                }))
              : undefined

          return {
            type: w.type,
            title: w.title || w.config?.title,
            parameters: sanitizeParams(params),
            handles: handles.length ? handles : undefined,
            onboarding,
          }
        })
        return {
          id: agent.id,
          title: agent.title,
          description: agent.description,
          bound_forms: (agent.config as any)?.forms || [],
          nodes,
        }
      },
    }),

    delete_form: tool({
      description: "Permanently delete a form. Requires confirm=true.",
      parameters: z.object({
        form_id: z.string().nullable(),
        form_title: z.string().nullable(),
        confirm: z.boolean().describe("Must be true to actually delete"),
      }),
      execute: ({ form_id, form_title, confirm }) => serialize(async () => {
        if (!confirm) return { error: "Deletion not confirmed. Ask the user to confirm, then pass confirm=true." }
        const found = await findForm(teamId, form_id, form_title)
        if ("error" in found) return found
        if (!(await approve({ kind: "delete_form", formTitle: found.form.title, summary: `permanently delete the form "${found.form.title}"`, detail: {} }))) return rejected
        const { error } = await supabase.from("forms").delete().eq("id", found.form.id)
        if (error) return { error: error.message }
        changed()
        return { success: true, deleted_form: found.form.title }
      }),
    }),
  }
}

const SECRET_KEY = /(api_?key|apikey|secret|token|password|authorization|bearer|private_?key|credential)/i
function sanitizeParams(value: any, depth = 0): any {
  if (depth > 6) return "[…]"
  if (value == null || typeof value !== "object") {
    if (typeof value === "string" && value.length > 4000) return value.slice(0, 4000) + "…[truncated]"
    return value
  }
  if (Array.isArray(value)) {
    const capped = value.slice(0, 200).map((v) => sanitizeParams(v, depth + 1))
    if (value.length > 200) capped.push(`…(+${value.length - 200} more)`)
    return capped
  }
  const out: Record<string, any> = {}
  for (const [k, v] of Object.entries(value)) {
    if (SECRET_KEY.test(k)) { out[k] = "[redacted]"; continue }
    out[k] = sanitizeParams(v, depth + 1)
  }
  return out
}

type FoundForm = { form: { id: string; title: string; fields: unknown } } | { error: string }

async function findForm(teamId: string | undefined, formId?: string, formTitle?: string): Promise<FoundForm> {
  if (!formId && !formTitle) return { error: "Provide form_id or form_title." }
  let q = supabase.from("forms").select("id, title, fields")
  if (teamId) q = q.eq("team", teamId)
  if (formId) q = q.eq("id", formId)
  const { data, error } = await q
  if (error) return { error: error.message }
  const rows = data || []
  let match = formId ? rows[0] : undefined
  if (!match && formTitle) {
    const needle = formTitle.toLowerCase()
    match = rows.find((r) => (r.title || "").toLowerCase().includes(needle))
  }
  if (!match) return { error: "Form not found." }
  return { form: { id: match.id, title: match.title || "(untitled)", fields: match.fields } }
}

// The full field guide, for when the forms page's instructions aren't loaded
export function buildFormGuideTool() {
  return {
    forms_guide: tool({
      description: "The full guide to Magic Form fields, auto-fill, conditions, formulas and groups. Read it before designing or changing fields.",
      parameters: z.object({}),
      execute: async () => ({ guide: buildFormSystemPrompt() }),
    }),
  }
}
